// Demo session hooks. The session itself lives in src/lib/session.ts; this file adds
// the react-query side: the persona list and cache handling around start, join,
// switch and leave.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { callRead, PtwError } from '@/lib/commands'
import { demoStatus, joinSandbox, leaveDemo, startDemo, switchPersona } from '@/lib/session'
import { useSession } from '@/lib/queries'

export type DemoStatus = NonNullable<Awaited<ReturnType<typeof demoStatus>>>

/** Personas of the current sandbox with their credentials and binding state. */
export function useDemoStatus(enabled = true) {
  const s = useSession()
  return useQuery({
    queryKey: ['demo-status', s.sandboxCode, s.activePersona],
    queryFn: () => demoStatus(),
    enabled: enabled && Boolean(s.sandboxCode && s.activePersona),
    staleTime: 15_000,
  })
}

/**
 * Session actions with cache handling. Query keys carry the persona code but not the
 * sandbox, so a new sandbox's PA must never be shown the previous sandbox's permits or
 * registry ids. Starting, joining or leaving therefore clears the cache first, and
 * afterwards resets whatever was read while the change was in flight (a reset keeps
 * mounted components attached and refetches what they show). Switching persona inside
 * one sandbox only invalidates: the keys already differ.
 */
export function useDemoActions() {
  const qc = useQueryClient()
  const start = useCallback(async () => {
    qc.clear()
    try {
      return await startDemo('PA')
    } finally {
      void qc.resetQueries()
    }
  }, [qc])
  const join = useCallback(
    async (sandboxCode: string, personaCode: string) => {
      qc.clear()
      try {
        await joinSandbox(sandboxCode, personaCode)
      } finally {
        void qc.resetQueries()
      }
    },
    [qc],
  )
  const switchTo = useCallback(
    async (personaCode: string) => {
      await switchPersona(personaCode)
      // If this persona was moved to another device, this browser's account for it is
      // disabled (PW004). Choosing it here again is an explicit request: re-bind it.
      try {
        await callRead('ptw_me')
      } catch (e) {
        if (PtwError.from(e).code !== 'PW004') throw e
        await switchPersona(personaCode, undefined, { force: true })
      }
      await qc.invalidateQueries()
    },
    [qc],
  )
  const leave = useCallback(async () => {
    qc.clear()
    try {
      await leaveDemo()
    } finally {
      void qc.resetQueries()
    }
  }, [qc])
  return { start, join, switchTo, leave }
}

/** Runs one async action at a time and keeps its error for display. */
export function useAsyncAction() {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const run = useCallback(async (key: string, fn: () => Promise<unknown>): Promise<boolean> => {
    setBusy(key)
    setError(null)
    try {
      await fn()
      return true
    } catch (e) {
      if (mounted.current) setError(e)
      return false
    } finally {
      if (mounted.current) setBusy(null)
    }
  }, [])
  return { busy, error, run, clearError: () => setError(null) }
}

type CopyState = 'idle' | 'copied' | 'failed'

/** Copies text to the clipboard; reports failure so the screen can say "copy it by hand". */
export function useCopy(): [CopyState, (text: string) => void] {
  const [state, setState] = useState<CopyState>('idle')
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(timer.current), [])
  const copy = useCallback((text: string) => {
    const done = (s: CopyState) => {
      setState(s)
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setState('idle'), 4000)
    }
    if (!navigator.clipboard?.writeText) {
      done('failed')
      return
    }
    navigator.clipboard.writeText(text).then(
      () => done('copied'),
      () => done('failed'),
    )
  }, [])
  return [state, copy]
}

/** A QR image (data URL) for a join link. Loaded on demand; null until ready or if it fails. */
export function useQrDataUrl(text: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    setUrl(null)
    if (!text) return
    import('qrcode')
      .then((mod) => {
        const api = (typeof mod.toDataURL === 'function' ? mod : (mod as unknown as { default: typeof mod }).default) as typeof mod
        return api.toDataURL(text, { margin: 1, width: 240, errorCorrectionLevel: 'M', color: { dark: '#1b1f3b', light: '#ffffff' } })
      })
      .then((data) => {
        if (!cancelled) setUrl(data)
      })
      .catch(() => {
        /* the code and link are still shown as text */
      })
    return () => {
      cancelled = true
    }
  }, [text])
  return url
}
